import React, { useMemo, useState } from "react";

const PROFILE_STORAGE_KEY = "vertex_profile";
const STATS_STORAGE_KEY = "vertex_offline_stats";

function safeParse(json, fallback) {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export default function Dashboard({ messages = [], onClose }) {
  const [profile, setProfile] = useState(() =>
    safeParse(localStorage.getItem(PROFILE_STORAGE_KEY) || "null", {
      name: "",
      company: "",
      role: "",
    })
  );

  const stats = useMemo(() => {
    const stored = safeParse(localStorage.getItem(STATS_STORAGE_KEY) || "null", {
      totalUserMessages: 0,
      totalBotMessages: 0,
      intents: {},
      lastActiveAt: null,
    });

    const last5 = messages.slice(-5).reverse();
    const total = messages.length;
    return { ...stored, last5, total };
  }, [messages]);

  const saveProfile = (next) => {
    setProfile(next);
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(next));
  };

  const resetStats = () => {
    const next = {
      totalUserMessages: 0,
      totalBotMessages: 0,
      intents: {},
      lastActiveAt: null,
    };
    localStorage.setItem(STATS_STORAGE_KEY, JSON.stringify(next));
    window.location.reload();
  };

  const topIntents = useMemo(() => {
    const entries = Object.entries(stats.intents || {});
    entries.sort((a, b) => (b[1] || 0) - (a[1] || 0));
    return entries.slice(0, 6);
  }, [stats.intents]);

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-lg">Personal Dashboard</h3>
        <button
          onClick={onClose}
          className="text-gray-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-700"
          title="Back to chat"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <section className="bg-gray-900/40 border border-gray-700 rounded-2xl p-4">
          <h4 className="text-white font-medium mb-3">Profile</h4>
          <div className="space-y-3">
            <label className="block">
              <span className="text-gray-300 text-sm">Your name</span>
              <input
                value={profile.name || ""}
                onChange={(e) => saveProfile({ ...profile, name: e.target.value })}
                placeholder="e.g., Ahmed"
                className="mt-1 w-full bg-gray-700 text-white placeholder-gray-400 px-3 py-2 rounded-xl border border-gray-600 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent focus:ring-opacity-20"
              />
            </label>
            <label className="block">
              <span className="text-gray-300 text-sm">Company (optional)</span>
              <input
                value={profile.company || ""}
                onChange={(e) => saveProfile({ ...profile, company: e.target.value })}
                placeholder="e.g., Vertex AI Tec"
                className="mt-1 w-full bg-gray-700 text-white placeholder-gray-400 px-3 py-2 rounded-xl border border-gray-600 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent focus:ring-opacity-20"
              />
            </label>
            <label className="block">
              <span className="text-gray-300 text-sm">Role (optional)</span>
              <input
                value={profile.role || ""}
                onChange={(e) => saveProfile({ ...profile, role: e.target.value })}
                placeholder="e.g., Founder / Support / Sales"
                className="mt-1 w-full bg-gray-700 text-white placeholder-gray-400 px-3 py-2 rounded-xl border border-gray-600 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent focus:ring-opacity-20"
              />
            </label>
          </div>
        </section>

        <section className="bg-gray-900/40 border border-gray-700 rounded-2xl p-4">
          <h4 className="text-white font-medium mb-3">Session stats</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-800/60 rounded-xl p-3 border border-gray-700">
              <div className="text-gray-300 text-xs">Messages (total)</div>
              <div className="text-white text-xl font-semibold">{stats.total}</div>
            </div>
            <div className="bg-gray-800/60 rounded-xl p-3 border border-gray-700">
              <div className="text-gray-300 text-xs">Last active</div>
              <div className="text-white text-sm font-medium">{formatDateTime(stats.lastActiveAt)}</div>
            </div>
            <div className="bg-gray-800/60 rounded-xl p-3 border border-gray-700">
              <div className="text-gray-300 text-xs">User messages</div>
              <div className="text-white text-xl font-semibold">{stats.totalUserMessages || 0}</div>
            </div>
            <div className="bg-gray-800/60 rounded-xl p-3 border border-gray-700">
              <div className="text-gray-300 text-xs">Bot messages</div>
              <div className="text-white text-xl font-semibold">{stats.totalBotMessages || 0}</div>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-gray-300 text-sm">Top topics</div>
              <button
                onClick={resetStats}
                className="text-xs text-gray-300 hover:text-white px-2 py-1 rounded-lg hover:bg-gray-700 border border-gray-700"
                title="Reset offline analytics"
              >
                Reset
              </button>
            </div>
            {topIntents.length === 0 ? (
              <div className="text-gray-400 text-sm">No topic data yet. Chat a bit first.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {topIntents.map(([k, v]) => (
                  <span
                    key={k}
                    className="text-xs bg-gray-700/70 text-gray-100 border border-gray-600 px-2 py-1 rounded-full"
                  >
                    {k} · {v}
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="sm:col-span-2 bg-gray-900/40 border border-gray-700 rounded-2xl p-4">
          <h4 className="text-white font-medium mb-3">Recent messages</h4>
          {stats.last5.length === 0 ? (
            <div className="text-gray-400 text-sm">No messages yet.</div>
          ) : (
            <div className="space-y-2">
              {stats.last5.map((m, idx) => (
                <div key={idx} className="flex items-start justify-between gap-3">
                  <div className="text-xs text-gray-400 w-14 shrink-0">{m.time || "—"}</div>
                  <div className="flex-1 text-sm text-gray-100">
                    <span className="text-gray-400 mr-2">{m.sender === "user" ? "You" : "Bot"}:</span>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

